// The HUD panels (SPEC §15 chrome, §16 register, §14 progression).
//
// Five panels and a reticle, and between them they are the entire heads-up
// display. The one that matters most is the smallest: §14 says "the number of
// clicks in your bank is the entire progression HUD", so BankPanel is not a
// widget among others — it is the whole personal layer, and everything else on
// screen is context for it.
//
// Register throughout is §16's: second person, addressed as Commander, every
// string an order or a report. "Bank empty, Commander. Hold." is a different
// message from "0 clicks available" and it is the same data.

import type { ReactNode } from 'react';
import { XStack, YStack, Stack } from 'tamagui';
import { Bar, Panel, RadialDial, Readout, UserText, Hairline } from './chrome';
import {
  LinkLabels,
  OutcomeLabels,
  RefusalLabels,
  countryName,
  countryNamed,
  feedText,
} from './labels';
import type { FeedEntry, HudSnapshot } from './hudState';
import { Buses, type Bus } from '../audio/mixer';
import { isFortress } from '../wire/core';

/**
 * The bank: §14's entire progression HUD.
 *
 * Capacity and refill are shown alongside the count because the count on its own
 * cannot say whether it is going up. A veteran's 40-cap bank and a newcomer's
 * 5-of-20 read identically at "3 clicks" without them.
 */
export function BankPanel({ snap }: { snap: HudSnapshot }): ReactNode {
  const { bank, bankKnown } = snap;
  const full = bank.capacity > 0 ? bank.banked / bank.capacity : 0;
  // Refill is counted down locally between SELF frames, so it ticks every second
  // rather than every two — which is why the dial is worth having at all.
  const refillFrac = bank.refillSeconds > 0 ? 1 - Math.min(1, bank.refillSeconds / 30) : 1;
  return (
    <Panel section="bank" width={230}>
      <XStack alignItems="center" gap="$sm">
        <Readout level="hero" tone={bank.banked === 0 ? 'warn' : 'data'}>
          {bankKnown ? String(bank.banked) : '--'}
        </Readout>
        <YStack flex={1} gap={2}>
          <Readout level="small" tone="dim">
            OF {bankKnown ? bank.capacity : '--'} CAPACITY
          </Readout>
          <Bar value={full} tone={bank.banked === 0 ? 'warn' : 'data'} />
        </YStack>
        <RadialDial value={refillFrac} tone={bank.banked === 0 ? 'warn' : 'primary'} />
      </XStack>
      <Readout level="small" tone="dim">
        {!bankKnown
          ? 'AWAITING QUARTERMASTER'
          : bank.banked >= bank.capacity
            ? 'BANK FULL, COMMANDER'
            : `NEXT CLICK IN ${bank.refillSeconds}s`}
      </Readout>
    </Panel>
  );
}

/**
 * The last verdict.
 *
 * §3 requires the three failure outcomes to look different, and the map already
 * does that in colour and shape. This panel is the second channel: the words. A
 * player who cannot tell an amber flash from a red one can still read "ORDER
 * REFUSED" and "OVERRUN", and §15's accessibility clause means that is not
 * optional.
 */
export function OutcomePanel({ snap }: { snap: HudSnapshot }): ReactNode {
  const tone: Record<string, 'data' | 'warn' | 'alert' | 'dim' | 'primary'> = {
    pending: 'primary',
    applied: 'data',
    captured: 'data',
    illegal: 'warn',
    lost: 'alert',
    dropped: 'dim',
    settled: 'dim',
  };
  const refusal = snap.refusal;
  const outcome = snap.lastOutcome;
  return (
    <Panel section="outcome" width={230}>
      {refusal ? (
        <>
          <Readout tone="warn">HELD</Readout>
          <Readout level="small" tone="dim">
            {RefusalLabels[refusal.reason] ?? refusal.reason}
          </Readout>
        </>
      ) : outcome ? (
        <>
          <Readout tone={tone[outcome.outcome] ?? 'dim'}>
            {OutcomeLabels[outcome.outcome]?.title ?? outcome.outcome.toUpperCase()}
          </Readout>
          <Readout level="small" tone="dim">
            {OutcomeLabels[outcome.outcome]?.detail ?? ''}
          </Readout>
        </>
      ) : (
        <Readout level="small" tone="dim">
          NO ORDERS ISSUED
        </Readout>
      )}
    </Panel>
  );
}

/**
 * The hovered pixel.
 *
 * §15: "Country identity must never rest on colour alone — flags carry pattern,
 * and the hovered pixel always names its owner in text." This panel is the
 * discharge of that requirement, which is why the owner line is the first thing
 * in it and is never abbreviated to a colour swatch.
 *
 * The name renders as USER TEXT, in the sans, because a country name is content
 * and not chrome — same rule as a comment.
 */
export function TargetPanel({ snap }: { snap: HudSnapshot }): ReactNode {
  const h = snap.hover;
  return (
    <Panel section="target" width={230}>
      {h ? (
        <>
          <UserText fontSize="$5">{countryName(h.owner)}</UserText>
          {!countryNamed(h.owner) ? (
            <Readout level="small" tone="dim">
              UNREGISTERED COUNTRY ID
            </Readout>
          ) : null}
          <Hairline />
          <XStack gap="$sm">
            <Readout level="small" tone="dim">
              X {String(h.x).padStart(3, '0')}
            </Readout>
            <Readout level="small" tone="dim">
              Y {String(h.y).padStart(3, '0')}
            </Readout>
            <Readout level="small" tone="dim">
              HP {String(h.hp).padStart(4, '0')}
            </Readout>
          </XStack>
          {isFortress(h.flags) ? <Readout level="small" tone="warn">FORTRESS</Readout> : null}
        </>
      ) : (
        <Readout level="small" tone="dim">
          NO TARGET
        </Readout>
      )}
    </Panel>
  );
}

/**
 * The field report.
 *
 * §16: "Every line is also a text event in the feed. Audio is never the only
 * channel for anything." That sentence is what this panel is for, and it is why
 * it exists in a build with no voice bank at all — the announcer is the optional
 * half.
 */
export function FeedPanel({ feed }: { feed: readonly FeedEntry[] }): ReactNode {
  return (
    <Panel section="feed" width={300}>
      <YStack gap={2}>
        {feed.length === 0 ? (
          <Readout level="small" tone="dim">
            NO CONTACT
          </Readout>
        ) : (
          feed.slice(0, 8).map((l) => (
            <XStack key={l.id} gap="$xs" alignItems="baseline">
              <Readout level="small" tone="primary">
                &gt;
              </Readout>
              {/* A feed line can carry server-written text, and the moment it can
                  carry a player's words it is user content. Sans, unstyled. */}
              <UserText fontSize="$3" flex={1}>
                {feedText(l)}
              </UserText>
            </XStack>
          ))
        )}
      </YStack>
    </Panel>
  );
}

/** The link, the tick, and whether the board is currently a lie. */
export function LinkPanel({ snap }: { snap: HudSnapshot }): ReactNode {
  const tone = snap.link === 'live' ? (snap.gapped ? 'warn' : 'data') : 'alert';
  return (
    <Panel section="link" width={230}>
      <XStack alignItems="center" gap="$sm">
        <Readout tone={tone}>{LinkLabels[snap.link] ?? snap.link.toUpperCase()}</Readout>
        <Stack flex={1} />
        <Readout level="small" tone="dim">
          T{String(snap.tick).padStart(7, '0')}
        </Readout>
      </XStack>
      {snap.gapped ? (
        <Readout level="small" tone="warn">
          FRAMES MISSED. AWAITING KEYFRAME.
        </Readout>
      ) : null}
      {snap.reject ? (
        <Readout level="small" tone="alert">
          {snap.reject.reason.toUpperCase()}
        </Readout>
      ) : null}
      <XStack alignItems="baseline" gap="$sm">
        <Readout level="small" tone="dim">
          COUNTRY
        </Readout>
        <UserText fontSize="$3">{countryName(snap.country)}</UserText>
        {!snap.countryLocked ? (
          <Readout level="small" tone="warn">
            UNPLACED
          </Readout>
        ) : null}
      </XStack>
      <Readout level="small" tone="dim">
        ARCS {String(snap.arcs).padStart(3, '0')}
      </Readout>
    </Panel>
  );
}

/**
 * Three sliders, and no fourth one.
 *
 * §16: "Separate volume sliders for music, effects, and voice, all persisted, all
 * defaulting to modest. Someone who mutes music entirely should still get the
 * announcer, because the announcer carries information." There is deliberately no
 * master mute here — one would be the obvious convenience and it is the exact bug
 * that sentence forbids.
 *
 * There is also no "enable sound" control. Audio unlocks on the first click on
 * the map; this panel says so rather than offering a button that would make the
 * elegant part pointless.
 */
export function AudioPanel({
  snap,
  onVolume,
  onReducedMotion,
}: {
  snap: HudSnapshot;
  onVolume: (bus: Bus, v: number) => void;
  onReducedMotion: (reduced: boolean) => void;
}): ReactNode {
  return (
    <Panel section="audio" width={230}>
      {!snap.audioLive ? (
        <Readout level="small" tone="dim">
          AUDIO ARMS ON YOUR FIRST ORDER
        </Readout>
      ) : null}
      {Buses.map((bus) => (
        <YStack key={bus} gap={2}>
          <XStack>
            <Readout level="small" tone="dim">
              {bus.toUpperCase()}
            </Readout>
            <Stack flex={1} />
            <Readout level="small" tone="dim">
              {String(Math.round(snap.volumes[bus] * 100)).padStart(3, '0')}
            </Readout>
          </XStack>
          {/*
            A native range input rather than a component: it is the one control in
            the interface that must be keyboard-operable and screen-reader-labelled
            without any work, and §15's accessibility clause is not negotiable. It
            is styled from the same tokens through the global sheet.
          */}
          <input
            type="range"
            className="wwp-slider"
            aria-label={`${bus} volume`}
            min={0}
            max={100}
            step={1}
            value={Math.round(snap.volumes[bus] * 100)}
            onChange={(e) => onVolume(bus, Number(e.currentTarget.value) / 100)}
          />
        </YStack>
      ))}
      <Hairline />
      <XStack alignItems="center" gap="$sm">
        <Readout level="small" tone="dim">
          REDUCED MOTION
        </Readout>
        <Stack flex={1} />
        <input
          type="checkbox"
          className="wwp-check"
          aria-label="reduced motion"
          checked={snap.reducedMotion}
          onChange={(e) => onReducedMotion(e.currentTarget.checked)}
        />
      </XStack>
      <Readout level="small" tone="dim">
        ARCS STAY. TRACER, SCANLINES, FLICKER STOP.
      </Readout>
    </Panel>
  );
}
